import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { StatusPill } from '@/components/px/StatusPill'
import { libraryNav } from '@/components/px/nav'
import { asApiError } from '@/services/apiError'
import { selectActiveOrgId } from '@/features/auth/slices/sessionSlice'
import { AgentSettingsForm } from '@/features/agentsettings/components/AgentSettingsForm'
import { AgentVersionHistory } from '@/features/agentsettings/components/AgentVersionHistory'
import {
  effectiveSourceOf,
  useAgentSettingsLabels,
} from '@/features/agentsettings/agentSettingsLabels'
import {
  agentSettingsEntryInvalidated,
  openAgentSettings,
  selectAgentSettingsError,
  selectAgentSettingsLoadedFor,
  selectAgentSettingsLoading,
  selectAgentSettingsVersions,
  selectFirmEffective,
} from '@/features/agentsettings/slices/agentSettingsSlice'
import {
  AGENT_PHASES,
  type AgentPhase,
  type AgentSettingsKey,
} from '@/features/agentsettings/types'
import {
  agentsRefetchArmed,
  openAgents,
  selectAgentById,
  selectAgentsError,
  selectAgentsLoading,
} from '@/features/agents/slices/agentsSlice'
import { useAppDispatch, useAppSelector } from '@/store'

const NO_VERSIONS: never[] = []

export default function AgentConfigPage() {
  const { t } = useTranslation('agents')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { agentId = '' } = useParams<{ agentId: string }>()
  const orgId = useAppSelector(selectActiveOrgId) ?? ''
  const agent = useAppSelector((state) => selectAgentById(state, agentId))
  const agentsLoading = useAppSelector(selectAgentsLoading)
  const agentsError = useAppSelector(selectAgentsError)
  const [phase, setPhase] = useState<AgentPhase>('discover')
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const settingsKey = useMemo<AgentSettingsKey | null>(
    () =>
      agent === undefined || orgId === ''
        ? null
        : { orgId, scope: 'FIRM', phase, agentKey: agent.id },
    [agent, orgId, phase]
  )
  const labels = useAgentSettingsLabels()
  // A reread of an already-loaded entry keeps the form mounted (and its save
  // confirmation visible); only a first read shows the loading panel.
  const settingsLoading = useAppSelector(
    (state) =>
      selectAgentSettingsLoading(state) &&
      (settingsKey === null ||
        !selectAgentSettingsLoadedFor(state, settingsKey))
  )
  // The list-level read error only: operation refusals live on their own
  // channel inside the form, so this retry is always reachable.
  const settingsError = useAppSelector(selectAgentSettingsError)
  const versions = useAppSelector((state) =>
    settingsKey === null
      ? NO_VERSIONS
      : selectAgentSettingsVersions(state, settingsKey)
  )
  const effective = useAppSelector((state) =>
    settingsKey === null ? null : selectFirmEffective(state, settingsKey)
  )

  const effectiveSourceKind =
    effective !== null && settingsKey !== null
      ? effectiveSourceOf(effective, versions, settingsKey.scope).kind
      : null

  useEffect(() => {
    if (orgId !== '') void dispatch(openAgents({ orgId }))
  }, [dispatch, orgId])

  useEffect(() => {
    if (settingsKey !== null) void dispatch(openAgentSettings(settingsKey))
  }, [dispatch, settingsKey])

  const retryAgents = () => {
    dispatch(agentsRefetchArmed())
    void dispatch(openAgents({ orgId }))
  }
  const retrySettings = () => {
    if (settingsKey === null) return
    dispatch(agentSettingsEntryInvalidated())
    void dispatch(openAgentSettings(settingsKey))
  }

  return (
    <AppShell
      moduleLabel={t('chrome.moduleLabel')}
      breadcrumb={
        <Link
          to="/library/agents"
          className="underline underline-offset-2 hover:no-underline"
        >
          {t('configuration.back')}
        </Link>
      }
      contextPill={t('chrome.configurationPill')}
      nav={libraryNav}
    >
      {agentsLoading ? (
        <LoadingPanel label={t('configuration.loadingAgent')} />
      ) : agentsError !== null ? (
        <ErrorPanel error={agentsError} onRetryClicked={retryAgents} />
      ) : agent === undefined ? (
        <EmptyPanel
          title={t('configuration.notFound')}
          body={t('configuration.notFoundBody')}
          actionLabel={t('configuration.back')}
          onActionClicked={() => navigate('/library/agents')}
        />
      ) : (
        <>
          <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
            <div>
              <h1 className="font-serif text-2xl font-semibold">
                {agent.name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                {agent.category !== undefined && (
                  <StatusPill tone="neutral">{agent.category}</StatusPill>
                )}
                <StatusPill tone={agent.isConfigured ? 'decided' : 'neutral'}>
                  {agent.isConfigured
                    ? t('catalogue.configured')
                    : t('catalogue.notConfigured')}
                </StatusPill>
                {effective !== null && settingsKey !== null && (
                  <span
                    data-testid="agent-effective-source"
                    data-source={effectiveSourceKind}
                  >
                    <StatusPill
                      tone={
                        effectiveSourceKind === 'published'
                          ? 'decided'
                          : 'neutral'
                      }
                    >
                      {labels.effectiveSource(
                        effective,
                        versions,
                        settingsKey.scope
                      )}
                    </StatusPill>
                  </span>
                )}
              </div>
            </div>
            <Link
              to={`/library/agents/${encodeURIComponent(agent.id)}/guardrails`}
              className="text-sm font-medium text-foreground underline underline-offset-2 hover:no-underline"
              data-testid="agent-guardrails-link"
            >
              {t('configuration.contextLink')} →
            </Link>
          </header>
          <label
            className="mt-5 block max-w-sm text-sm font-medium"
            htmlFor="agent-phase"
          >
            {t('configuration.phase')}
            <select
              id="agent-phase"
              value={phase}
              onChange={(event) => {
                setSelectedVersion(null)
                setPhase(event.target.value as AgentPhase)
              }}
              className="mt-1 h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            >
              {AGENT_PHASES.map((item) => (
                <option key={item} value={item}>
                  {t(`phases.${item}`)}
                </option>
              ))}
            </select>
          </label>
          {settingsLoading ? (
            <div className="mt-5">
              <LoadingPanel label={t('configuration.loadingSettings')} />
            </div>
          ) : settingsError !== null ? (
            <div className="mt-5">
              <ErrorPanel
                error={asApiError({
                  status: settingsError.status,
                  key: settingsError.code,
                  message: settingsError.message,
                })}
                onRetryClicked={retrySettings}
              />
            </div>
          ) : settingsKey !== null ? (
            <>
              <AgentSettingsForm settingsKey={settingsKey} agent={agent} />
              <AgentVersionHistory
                versions={versions}
                selectedVersion={selectedVersion}
                onChange={setSelectedVersion}
              />
            </>
          ) : null}
        </>
      )}
    </AppShell>
  )
}
